/**
 * English diagnostic message templates, keyed by `msgKey`. ICU MessageFormat
 * syntax (`{name}`, `{count, plural, …}`, `{kind, select, …}`). This is the
 * authoritative key set every locale mirrors; `catalog.ru.ts` follows it and
 * the completeness test enforces parity.
 *
 * Keys are namespaced by the area that raises them (resolver.*, view.*,
 * validation.*, usePolicy.*, parse.*). A LITERAL brace is written `\{` / `\}`
 * — bare `{ }` is consumed by ICU, and `{{ }}` is not an escape (it throws, and
 * the raw template then leaks every placeholder to the user). Apostrophes are
 * auto-doubled by the renderer, and `<…>` is treated literally (ignoreTag).
 */

export const DIAG_EN: Record<string, string> = {
  // --- resolver.* -----------------------------------------------------------
  "resolver.duplicateType": "Duplicate type declaration: '{name}'",
  // Decorations
  "resolver.duplicateDecoration": "Duplicate decoration declaration: '{name}'",
  "resolver.decorationRedeclaresBuiltin":
    "'{name}' is a built-in decoration and cannot be redeclared",
  "validation.decorationUnknown":
    "Unknown decoration '@{name}' — declare it with `decoration {name} \\{ … \\}`",
  "validation.decorationIsLifecycleState":
    "'@{name}' is a lifecycle state, not a decoration — states are derived from the '@planned' and '@deprecated' markers, so an element is {name} at the points those windows put it there",
  "validation.decorationBadTarget":
    "'@{name}' cannot decorate a {kind} — it declares targets: {targets}",
  "validation.decorationUnknownArg": "'@{name}' has no field '{arg}'",
  "validation.decorationDuplicateArg": "'@{name}' sets '{arg}' twice",
  "validation.decorationReservedField":
    "'targets' is the reserved meta-field of a decoration — a schema field cannot use that name",
  "validation.decorationHomonym":
    "Field '{name}' shares its name with a decoration — '@{name}' reads the decoration, so this field is unreachable through a getter. Rename the field.",
  "validation.lifecycleWindowEmpty":
    "'{name}' is retired before it is born — the lifecycle window is empty",
  "validation.lifecycleWindowInverted":
    "'@{name}' has 'from' after 'to' — the transition window runs backwards, so its transition zone never occurs",
  "validation.lifecycleDuplicate":
    "'@{name}' is applied to '{owner}' more than once — an element carries one '@{name}' window",
  "validation.lifecycleOutlivesContainer":
    "'{name}' is declared to outlive its container '{container}' — a child cannot be present when its container is not",
  "validation.lifecycleMalformedDate":
    "'{text}' is not a valid date — use 2026, 2026-Q3, 2026-08 or 2026-08-17",
  "validation.lifecycleMixedMarkers":
    "The timeline mixes {families} markers — they order deterministically against each other, but the order between families is by convention (dates first), not by meaning",
  "validation.lifecycleRefDeprecated":
    "'{target}' is deprecated{until}{useHint}",
  "validation.lifecycleRefRetired":
    "'{target}' is retired as of {at}, but is still referenced here",
  "validation.lifecycleRefPlanned":
    "'{target}' is planned and not yet live — a live element cannot depend on it",
  "validation.lifecycleSunsetScheduled": "'{name}' is scheduled to sunset from {at}",
  "resolver.duplicateModule":
    "Duplicate top-level module '{name}' — a module with this name is already declared. A module name is unique within its scope; use 'in {parent}' to extend a module across files.",
  "resolver.duplicateDeclaration":
    "Duplicate {kind} '{name}' — a {kind} with this name is already declared in this scope.",
  "resolver.aspectDropNotInherited": "Aspect drop '{key}' has no matching inherited aspect",
  "resolver.inTargetUnknownTarget":
    "'in {key}' references an unknown{ambiguous, select, true{ (ambiguous)} other{}} target module",
  "resolver.inTargetAmbiguous":
    "'in {key}' is ambiguous — candidates: {candidates}. Qualify the name or use a stable id.",
  "resolver.inTargetUnknownParent": "'in {key}' references unknown parent module",
  "resolver.typeProcessViewForbidden":
    "'type {parentName}' is not allowed; {parentName} subtyping is reserved",
  "resolver.unknownParentType": "Unknown or cyclic parent type '{parentType}' for type '{name}'",
  "resolver.dropNotInherited":
    "'drop {target}' has no matching inherited entity — drop applies only to inherited entities",
  "resolver.cascadeModeRedefined": "Cannot change cascade mode of inherited field '{field}'",
  "resolver.overrideNotInheritedSurface": "'override' on '{name}' has no matching inherited entity",
  "resolver.typeChangeNeedsOverrideSurface":
    "Cannot change type of '{name}' from '{from}' to '{to}' without 'override'",
  "resolver.overrideNotInheritedInterface":
    "'override' on '{name}' has no matching inherited interface",
  "resolver.typeChangeNeedsOverrideInterface":
    "Cannot change kind of '{name}' from '{from}' to '{to}' without 'override'",
  "resolver.overrideNotInheritedModule": "'override' on '{name}' has no matching inherited module",
  "resolver.typeChangeNeedsOverrideModule":
    "Cannot change kind of '{name}' from '{from}' to '{to}' without 'override'",
  "resolver.unknownTypeModule":
    "Unknown type '{typeName}' — defaulting to 'module'. Declare with 'type module {typeName} \\{ … \\}' to silence this warning.{hasImport, select, true{ — or import it: use {typeName} from {pkg}} other{}}",
  "resolver.unknownTypeSurface": "Unknown surface type '{typeName}'",
  "resolver.wrongBaseTypeSurface": "Type '{typeName}' is not a surface type (base: {baseType})",
  "resolver.surfaceContainsModule": "Surface '{name}' must not contain modules",
  "resolver.unknownTypeInterface": "Unknown interface type '{typeName}'",
  "resolver.wrongBaseTypeInterface": "Type '{typeName}' is not an interface type (base: {baseType})",
  "resolver.multiOpOnTarget":
    "Multiple operations on '{key}' — at most one is allowed (across the primary body and any extension blocks)",

  // --- view.* ---------------------------------------------------------------
  "view.selectorError": "{detail}",
  "view.onNoPresence": "`on` cannot narrow: no plane-presence data in scope",
  "view.unknownPlane": "unknown plane `{plane}`",
  "view.unknownInTarget": "`in {name}` names nothing in scope — an unknown name, or a short name that several elements share (qualify it)",
  "view.uncontainableInTarget": "`in {name}` names no container in this model — nothing it matches can hold elements inside it",
  "view.emptyFocus": "`focus` matches nothing in the model — the view emphasises nothing",
  "view.groupByGetterError": "`group by` getter cannot evaluate here — {detail}",
  "view.unknownKnob": "binding `{name}` names no knob declared by `{view}`",
  "view.knobTypeNumber": "knob `{name}` expects a number, got {got}",
  "view.knobRangeMin": "knob `{name}` = {value} is below min {min}",
  "view.knobRangeMax": "knob `{name}` = {value} is above max {max}",
  "view.knobRangeStep": "knob `{name}` = {value} is not aligned to step {step} from {base}",
  "view.knobTypeElementRef": "knob `{name}` expects an element ref, got {got}",
  "view.knobTypeNoElement": "knob `{name}` — no element `{path}` in scope",
  "view.knobTypeNotElementType": "knob `{name}` — `{path}` is not a `{elementType}`",
  "view.knobSetNotFrom": "knob `{name}` = `{value}` is not among the values of `{getter}`",
  "view.knobSetOneOf": "knob `{name}` takes a single value (`one of`)",
  "view.knobSetNotEnum": "knob `{name}` = `{value}` is not in {allowed}",
  "view.unknownParent": "unknown parent view `{parent}` for instance `{instance}`",
  "view.multipleRepresentations":
    "instance `{instance}` has {count, plural, one {# representation clause} other {# representation clauses}} after merging with `{parent}` — at most one ({kinds})",
  "view.unknownPolicy": "unknown policy `{name}` in `violating` — no such policy in scope",
  "view.unknownBundle": "unknown style bundle `{bundle}`",
  "view.cssUnparseable": "`{text}` is not a recognized CSS color",
  "view.cssEdgeNeedsLiteral": "css(…) on an edge needs a literal CSS color",
  "view.colorNeedsCastGetter":
    "`{getter}` is a value, not a color — wrap it in a cast, e.g. `colorize({getter})`",
  "view.colorNeedsCast":
    "a `color` value must be a cast (colorize / heat / css) or a literal CSS color",

  // --- field.* (§4.3 getters + casts in field values) ------------------------
  "field.refCycle": "`{path}` refers to itself through another field — no value can be resolved",
  "field.getterMissing": "`{getter}` resolves to nothing on this element",
  "field.colorNeedsCast":
    "`{path}` is a color, but `{expr}` is the value `{value}` — derive a color from it with `colorize({expr})`",
  "field.unknownColor": "`{value}` is not a palette color name or a CSS color",
  "field.castNeedsView":
    "`{cast}` assigns colors across a set of values, so it only works in a view's `style` body — use `colorize` for a stable per-value color",
  "field.castBadArg": "`{cast}(…)` was given arguments it cannot use",
  "field.exprUnsupported": "`{expr}` is not usable as a field value",

  // --- validation.* ---------------------------------------------------------
  "validation.reservedModuleName": "'{name}' is a reserved keyword and cannot be used as a module name",
  "validation.reservedSurfaceName": "'{name}' is a reserved keyword and cannot be used as a surface name",
  "validation.reservedInterfaceName":
    "'{name}' is a reserved keyword and cannot be used as an interface name",
  "validation.reservedTypeName": "'{name}' is a reserved keyword and cannot be used as a type name",
  "validation.latencyIsList":
    "latency is a single duration or a range, not a list — write '50ms-500ms', not '50ms, 500ms'",
  "validation.latencyBadValue":
    "latency value '{raw}' is not a valid duration — use a time unit (ms/s/min/h/d/wk/mo/y), e.g. '200ms' or '50ms-500ms'",
  "validation.latencyIncomplete":
    "'{name}' has no 'latency' but is called in a partly-timed process — add one to complete the estimate (or drop the others to opt out)",
  "validation.presenceRefUnresolved":
    "'{ref}' is not a member of '{owner}' — a presence block '{onClause}' projects members declared in the body; declare it, or fix the name",
  "validation.presenceMemberCollision":
    "'{member}' is declared both in the body of '{owner}' and in a presence block '{onClause}' — a plane-exclusive member shares one namespace with the main body, so one of the two values would be lost; rename it, or drop the duplicate",
  "validation.inclusionUnresolved":
    "inclusion target '{target}' is not defined — declare the module or remove the inclusion",
  "validation.aspectSamePlane":
    "'{member}' is already on plane '{plane}' — this membership crosses nothing; drop the aspect, or give the member its presence through this place alone",
  "validation.aspectInstanceNotModule":
    "'as {instance}' is not allowed on a {ownerType} — membership, and the instances that name it, is a module concept; '{ownerKey}' can only be classified",
  "validation.aspectInstanceOnString":
    "'as {instance}' needs a module place — '{key}' is quoted, and an implicit place has no scope to name an occupant in; name a real module, or fold the role into the string",
  "validation.instanceShadowsModule":
    "Instance name '{instance}' shadows a module of the same name — an instance actor resolves first; rename the instance to avoid confusion",
  "validation.instanceNameCollision":
    "Instance name '{instance}' at '{placeKey}' is already bound to '{prior}' — node-local instance names must be unique per place",
  "validation.duplicateAspectInstance":
    "Duplicate aspect instance '{path}{hasInstance, select, yes{ as {instance}} other{}}' — an identical membership is declared twice",
  "validation.duplicateRosterEntry":
    "Duplicate roster entry '{path}{hasInstance, select, yes{ as {instance}} other{}}' — an identical membership is declared twice",
  "validation.rosterMemberConflict":
    "'{member}' is named '{instance}' here but '{priorInstance}' on its own '{plane}' aspect — the member and roster disagree on the instance name",
  "validation.surfaceOnIgnored":
    "'on' presence in a surface body is ignored — presence is a module-level fact; move it to the owning module",
  "validation.planeMismatch":
    "'{key}' has no presence on this flow's plane ({planes}) — add presence ('on {firstPlane}') or move the step",
  "validation.unnamedBadDerivedName":
    "Cannot derive a name for an unnamed '{typeName}' — the type name has no usable letters; give the module an explicit name",
  "validation.unnamedBaseModule":
    "A base 'module' cannot be unnamed — give it a name, or use a custom type whose name can stand in",
  "validation.unnamedDuplicateType":
    "Type '{typeName}' is used more than once under the same parent — an unnamed module is allowed only when its type is used exactly once; give every '{typeName}' a name",
  "validation.unnamedSurface": "Only modules may be unnamed — the surface '{typeName}' needs a name",
  "validation.unnamedInterfaceDefault":
    "An unnamed interface must be a default — write '{decl}' to declare one, or give it a name",
  "validation.defaultInterfaceDuplicate":
    "A module may declare only one default interface — '{typeName}' is a second unnamed interface",
  "validation.duplicateInterface":
    "Two interfaces named '{name}' in '{module}' merge into one — rename one, or drop the duplicate",
  "validation.aspectBadRef":
    "aspect '{key}' references unknown module '{name}' — quote it (\"{name}\") to make it an aspect instead",
  "validation.widgetTagInvalid":
    "widget tag '{tag}' is not a valid custom-element name (must start with a lowercase letter and contain at least one hyphen — e.g. 'arch-module')",
  "validation.descUnresolvedRef": "Description reference '{text}' does not resolve to any declaration",
  "validation.descAmbiguousRef":
    "Description reference '{text}' is ambiguous — candidates: {names}. Use [[#id]] to disambiguate.",
  "validation.descMissingAspect": "Description references aspect '{text}' which is not set on this node",
  "validation.descMissingField": "Description references field '{text}' which is not set on this node",
  "validation.docBareInterpolation":
    "'{text}' is a bare interpolation, which has no owning node in a document — use a foreign getter '[[Element]]{text}' instead",
  "validation.descCalcError": "Calc: {message}{hasText, select, yes{ ('{text}')} other{}}",
  "validation.requiredNotFilled":
    "Required {kind} '{name}' on '{owner}' is not filled — provide a value or 'drop' it",
  "validation.requiredNotFulfilled":
    "Required {kind} '{name}' on '{owner}' is not fulfilled — add content or 'drop' it",
  "validation.requiredHasContent":
    "Required {kind} '{name}' on '{owner}' is a contradiction — a 'required' blank cannot have content; remove 'required' or the body",
  "validation.processOwnerTodo":
    "This {label} has no owner — say who decides/coordinates it (an owner prefix, or declare the process inside its owning module)",
  "validation.processDuplicateAs":
    "duplicate `as {name}` — re-entry aspects must be unique within a process, or `go {name}` is ambiguous",
  "validation.processFinishAllNoCompensation":
    "`finish all` inside a reversible skips compensation — completed steps' `unwind`s will NOT fire (use `fail all` to roll back)",
  "validation.processGoUnreachable":
    "`go {name}` targets a step that only occurs after a `fail all`/`finish all` — the re-entry point is unreachable",
  "validation.processGoTodo":
    "`go {name}` has no matching `as {name}` step in this process — name a step with `as {name}`, or fix the target",
  "validation.processFieldAfterAction":
    "Field '{field}' appears after the first action — process fields live in the header (before any step)",
  "validation.crossSpaceCoupling":
    "Cross-space coupling: 'do {qkey}' splices the {targetLabel} '{qkey}' in space '{tgtSpace}' from space '{refSpace}', which is not 'export'ed. Mark the {targetLabel} 'export' (top-level declarations only), or route it through an exported gateway.",
  "validation.processDoShadowed":
    "'do {qkey}' resolves to the subprocess '{last}' — a process shares the name and is shadowed",
  "validation.processDoProcessCaller":
    "'do {qkey}': a process declares no caller parameter — the '{caller} do' prefix has no effect (use a subprocess with 'on Caller' for parametrization)",
  "validation.processBadDoTarget":
    "'do {qkey}': no subprocess or process '{qkey}' is defined yet (the missing target stubs as a subprocess){hint}",
  "validation.processAnonHeadAtRoot":
    "A step with no caller ('> …') needs an enclosing module — declare the process inside the module that performs it, or write an explicit caller",
  "validation.snakeNoTail":
    "'\\' (snake) has no run to continue — the step before it has no callee to continue FROM (only a call, or a note/prose/'do'/'go' sitting between them, keeps a run alive). Write the caller explicitly, or move the line after the call it continues",
  "validation.processBadCaller":
    "Process caller '{caller}' is an interface — a caller must be a module or actor",
  "validation.processRoleConflictCaller": "'{name}' is used as both a module and an interface — these conflict",
  "validation.processCallerTodo":
    "Process caller '{caller}' is not defined yet — declare a module '{caller}' (or an actor), or route the step from an existing module",
  "validation.ambiguousModuleRefCaller":
    "Process caller '{caller}' is ambiguous — candidates: {candidates}. Qualify the name or use a stable id.",
  "validation.processRoleConflictCallee": "'{name}' is used as both an interface and a module — these conflict",
  "validation.processCalleeUnspecified":
    "Process callee '{callee}' has no specified interface yet — a handler interface stands in until you name one",
  "validation.processCalleeTodo":
    "Process callee '{callee}' is not defined yet — declare the interface '{callee}', or point the step at an existing one{hint}",
  "validation.ambiguousModuleRefCallee":
    "Process callee module '{name}' is ambiguous — candidates: {candidates}. Qualify the name or use a stable id.",
  "validation.ambiguousInterfaceLeaf":
    "Process callee '{written}' is ambiguous — candidates: {candidates}. Qualify the leaf with its surface path.",

  // --- usePolicy.* ----------------------------------------------------------
  "usePolicy.packageNotFound":
    "Cannot find package '{pkgName}' for `use policy {name}`. Add it to dependencies in package.archspace.",
  "usePolicy.notExported":
    "Policy '{name}' exists in package '{pkgName}' but is not exported. Add the 'export' keyword to its declaration.",
  "usePolicy.notFound": "Policy '{name}' not found in package '{pkgName}'.",
};
